// PEG (Gastrostomia) section for the care program.
// Feeding log + tube/stoma care + shift totals + alert banner.
// Follows the same pattern as PeritonealDialysisSection.

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2, Plus, AlertTriangle, Utensils, Send, Scale } from "lucide-react";
import { toast } from "sonner";
import {
  METHOD_LABELS,
  TOLERANCE_LABELS,
  STOMA_LABELS,
  PATENCY_LABELS,
  RESIDUAL_ALERT_ML,
  ASPIRATION_TOLERANCES,
  feedingCriticalReasons,
  tubeCareCriticalReasons,
  summarizeGastrostomyShift,
  detectGastrostomyCriticalReasons,
} from "@/lib/gastrostomy-alerts";
import {
  getSupervisorPhone,
  openSupervisorWhatsApp,
} from "@/lib/critical-alert";

function toIntOrNull(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = parseInt(String(v).replace(/[^0-9-]/g, ""), 10);
  return Number.isFinite(n) ? n : null;
}

function nowHM() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

// Short, focused WhatsApp message for a critical PEG event.
function buildPegAlertMessage({ patientName, address, nurseName, title, detailLines, reasons }) {
  const lines = [];
  lines.push("ALERTA PEG - Ciudad Enfermeria");
  lines.push("");
  lines.push(`Paciente: ${patientName || "-"}`);
  if (address) lines.push(`Direccion: ${address}`);
  if (nurseName) lines.push(`Enfermera: ${nurseName}`);
  lines.push(`Hora del aviso: ${nowHM()}`);
  lines.push("");
  if (title) lines.push(title);
  (detailLines || []).forEach((l) => lines.push(l));
  lines.push("");
  lines.push("Hallazgos:");
  (reasons || []).forEach((r) => lines.push(`- ${r}`));
  lines.push("");
  lines.push("Requiere valoracion inmediata.");
  return lines.join("\n");
}

function fireWhatsApp(msg) {
  try {
    const phone = getSupervisorPhone();
    const opened = openSupervisorWhatsApp(msg, phone);
    if (opened) {
      toast.warning(
        phone
          ? "Alerta enviada al supervisor por WhatsApp"
          : "Se abrio WhatsApp - selecciona al supervisor",
        { duration: 5000 }
      );
    } else {
      toast.error(
        "El navegador bloqueo la ventana. Usa el boton verde de WhatsApp abajo.",
        { duration: 6000 }
      );
    }
  } catch (err) {
    console.warn("Auto WhatsApp PEG alert failed", err);
  }
}

const EMPTY_TUBE_CARE = {
  stoma_status: [],
  external_length_cm: "",
  tube_rotated: null,
  stoma_cleaned: null,
  patency: "",
  notes: "",
};

export default function GastrostomySection({
  g,
  onChange,
  patientName = "",
  patientAddress = "",
  nurseName = "",
}) {
  const data = g || { feedings: [], tube_care: {}, weight_kg: "", observations: "" };
  const feedings = Array.isArray(data.feedings) ? data.feedings : [];
  const tubeCare = { ...EMPTY_TUBE_CARE, ...(data.tube_care || {}) };
  const totals = summarizeGastrostomyShift(data);
  const reasons = detectGastrostomyCriticalReasons(data);

  const addFeeding = () => {
    const next = [
      ...feedings,
      {
        id: `peg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        number: feedings.length + 1,
        time: "",
        formula_type: "",
        volume_ml: null,
        method: "",
        residual_ml: null,
        flush_before_ml: null,
        flush_after_ml: null,
        tolerance: "bien",
        head_elevated: null,
        notes: "",
      },
    ];
    onChange({ ...data, feedings: next });
  };

  const updateFeeding = (idx, key, value) => {
    const prev = feedings[idx] || {};
    const nextRow = { ...prev, [key]: value };

    // Auto-alert (synchronous, user gesture): tolerance became aspiration risk
    // or gastric residual exceeded the limit, and this row hasn't alerted yet.
    const becameCritical =
      !prev.alerted_at &&
      ((key === "tolerance" && ASPIRATION_TOLERANCES.includes(value)) ||
        (key === "residual_ml" && Number(value) > RESIDUAL_ALERT_ML));
    if (becameCritical) {
      nextRow.alerted_at = new Date().toISOString();
      const msg = buildPegAlertMessage({
        patientName,
        address: patientAddress,
        nurseName,
        title: `Toma #${nextRow.number || "?"} (${nextRow.time || "--:--"})`,
        detailLines: [
          nextRow.formula_type ? `Formula: ${nextRow.formula_type}` : "",
          nextRow.volume_ml ? `Volumen: ${nextRow.volume_ml} ml` : "",
        ].filter(Boolean),
        reasons: feedingCriticalReasons(nextRow),
      });
      fireWhatsApp(msg);
    }

    const next = feedings.map((f, i) => (i === idx ? nextRow : f));
    onChange({ ...data, feedings: next });
  };

  const removeFeeding = (idx) => {
    const next = feedings
      .filter((_, i) => i !== idx)
      .map((f, i) => ({ ...f, number: i + 1 }));
    onChange({ ...data, feedings: next });
  };

  const updateTubeCare = (key, value) => {
    const nextTc = { ...tubeCare, [key]: value };

    // Auto-alert for critical tube findings (obstruction, accidental removal,
    // purulent secretion, red + painful stoma). Alerts once per program.
    const criticalNow = tubeCareCriticalReasons(nextTc);
    const criticalBefore = tubeCareCriticalReasons(tubeCare);
    if (!tubeCare.alerted_at && criticalNow.length > 0 && criticalNow.length > criticalBefore.length) {
      nextTc.alerted_at = new Date().toISOString();
      const msg = buildPegAlertMessage({
        patientName,
        address: patientAddress,
        nurseName,
        title: "Cuidados de la sonda / estoma",
        detailLines: [
          nextTc.external_length_cm ? `Marca externa: ${nextTc.external_length_cm} cm` : "",
        ].filter(Boolean),
        reasons: criticalNow,
      });
      fireWhatsApp(msg);
    }

    onChange({ ...data, tube_care: nextTc });
  };

  const toggleStoma = (key) => {
    const current = Array.isArray(tubeCare.stoma_status) ? tubeCare.stoma_status : [];
    let next;
    if (key === "sano") {
      // "Sano" is exclusive: clears other findings.
      next = current.includes("sano") ? [] : ["sano"];
    } else if (current.includes(key)) {
      next = current.filter((s) => s !== key);
    } else {
      next = [...current.filter((s) => s !== "sano"), key];
    }
    updateTubeCare("stoma_status", next);
  };

  const feedingIsCritical = (f) => feedingCriticalReasons(f).length > 0;

  return (
    <div className="space-y-4" data-testid="peg-section">
      {/* Header row: peso corporal */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="sm:col-span-1">
          <Label className="mb-1 flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-slate-500">
            <Scale className="h-3 w-3" /> Peso del paciente (kg)
          </Label>
          <Input
            type="text"
            inputMode="decimal"
            value={data.weight_kg || ""}
            onChange={(e) => onChange({ ...data, weight_kg: e.target.value })}
            placeholder="65.8"
            className="h-10 text-base"
            data-testid="peg-weight"
          />
          <p className="mt-0.5 text-[10px] italic text-slate-500">
            Seguimiento nutricional: pesar en las mismas condiciones cada dia.
          </p>
        </div>
      </div>

      {/* Alert banner if any critical finding */}
      {reasons.length > 0 && (
        <div
          className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-800"
          data-testid="peg-alert-banner"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <p className="font-bold">Hallazgo critico PEG:</p>
            <ul className="mt-0.5 list-inside list-disc text-[13px]">
              {reasons.slice(0, 4).map((r, i) => (
                <li key={i}>{r}</li>
              ))}
              {reasons.length > 4 && <li>+{reasons.length - 4} mas</li>}
            </ul>
          </div>
        </div>
      )}

      {/* ------------------------- Feeding log ------------------------- */}
      <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
        <Utensils className="h-3.5 w-3.5" /> Bitacora de tomas
      </p>

      {feedings.length === 0 && (
        <div className="rounded-xl border-2 border-dashed border-slate-200 bg-white px-4 py-8 text-center">
          <p className="text-sm font-semibold text-slate-700">Sin tomas registradas</p>
          <p className="mt-0.5 text-xs text-slate-500">
            Toca &quot;Agregar toma&quot; para registrar cada alimentacion por sonda.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {feedings.map((f, idx) => {
          const isCritical = feedingIsCritical(f);
          return (
            <div
              key={f.id || idx}
              className={`rounded-xl border p-3 shadow-sm ${
                isCritical ? "border-red-300 bg-red-50/40" : "border-slate-200 bg-white"
              }`}
              data-testid={`peg-row-${idx}`}
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-amber-500 text-xs font-bold text-white">
                  {f.number || idx + 1}
                </span>
                {f.alerted_at && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800"
                    data-testid={`peg-alerted-${idx}`}
                    title={`Alerta WhatsApp enviada ${new Date(f.alerted_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}`}
                  >
                    <Send className="h-3 w-3" /> Supervisor avisado
                  </span>
                )}
                <div className="flex-1" />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 hover:bg-red-100"
                  onClick={() => removeFeeding(idx)}
                  data-testid={`peg-remove-${idx}`}
                >
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
                <div>
                  <Label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Hora</Label>
                  <Input
                    type="time"
                    value={f.time || ""}
                    onChange={(e) => updateFeeding(idx, "time", e.target.value)}
                    className="h-9 text-sm"
                    data-testid={`peg-time-${idx}`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Formula / alimento</Label>
                  <Input
                    type="text"
                    value={f.formula_type || ""}
                    onChange={(e) => updateFeeding(idx, "formula_type", e.target.value)}
                    placeholder="Ensure, licuado, polimerica..."
                    className="h-9 text-sm"
                    data-testid={`peg-formula-${idx}`}
                  />
                </div>
                <div>
                  <Label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Volumen ml</Label>
                  <Input
                    type="number"
                    inputMode="numeric"
                    value={f.volume_ml === "" || f.volume_ml == null ? "" : f.volume_ml}
                    onChange={(e) => updateFeeding(idx, "volume_ml", toIntOrNull(e.target.value))}
                    placeholder="250"
                    className="h-9 text-sm"
                    data-testid={`peg-volume-${idx}`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Metodo</Label>
                  <Select
                    value={f.method || ""}
                    onValueChange={(v) => updateFeeding(idx, "method", v)}
                  >
                    <SelectTrigger className="h-9 text-sm" data-testid={`peg-method-${idx}`}>
                      <SelectValue placeholder="Metodo" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(METHOD_LABELS).map(([k, label]) => (
                        <SelectItem key={k} value={k}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-6">
                <div>
                  <Label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Residuo ml</Label>
                  <Input
                    type="number"
                    inputMode="numeric"
                    value={f.residual_ml === "" || f.residual_ml == null ? "" : f.residual_ml}
                    onChange={(e) => updateFeeding(idx, "residual_ml", toIntOrNull(e.target.value))}
                    placeholder="0"
                    className={`h-9 text-sm ${Number(f.residual_ml) > RESIDUAL_ALERT_ML ? "border-red-400 bg-red-50 font-bold text-red-800" : ""}`}
                    data-testid={`peg-residual-${idx}`}
                  />
                </div>
                <div>
                  <Label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Agua antes ml</Label>
                  <Input
                    type="number"
                    inputMode="numeric"
                    value={f.flush_before_ml === "" || f.flush_before_ml == null ? "" : f.flush_before_ml}
                    onChange={(e) => updateFeeding(idx, "flush_before_ml", toIntOrNull(e.target.value))}
                    placeholder="30"
                    className="h-9 text-sm"
                    data-testid={`peg-flush-before-${idx}`}
                  />
                </div>
                <div>
                  <Label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Agua despues ml</Label>
                  <Input
                    type="number"
                    inputMode="numeric"
                    value={f.flush_after_ml === "" || f.flush_after_ml == null ? "" : f.flush_after_ml}
                    onChange={(e) => updateFeeding(idx, "flush_after_ml", toIntOrNull(e.target.value))}
                    placeholder="30"
                    className="h-9 text-sm"
                    data-testid={`peg-flush-after-${idx}`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Tolerancia</Label>
                  <Select
                    value={f.tolerance || "bien"}
                    onValueChange={(v) => updateFeeding(idx, "tolerance", v)}
                  >
                    <SelectTrigger
                      className={`h-9 text-sm ${f.tolerance && f.tolerance !== "bien" ? "border-red-400 bg-red-50 font-semibold text-red-800" : ""}`}
                      data-testid={`peg-tolerance-${idx}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(TOLERANCE_LABELS).map(([k, label]) => (
                        <SelectItem key={k} value={k}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Cabecera 30-45&deg;</Label>
                  <button
                    type="button"
                    onClick={() => updateFeeding(idx, "head_elevated", f.head_elevated === true ? null : true)}
                    className={`flex h-9 w-full items-center justify-center rounded-md border text-xs font-bold transition-colors ${
                      f.head_elevated
                        ? "border-emerald-400 bg-emerald-50 text-emerald-800"
                        : "border-slate-200 bg-white text-slate-500 hover:border-emerald-400"
                    }`}
                    data-testid={`peg-head-${idx}`}
                  >
                    {f.head_elevated ? "Si, elevada" : "Marcar"}
                  </button>
                </div>
              </div>

              <div className="mt-2">
                <Label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Notas</Label>
                <Input
                  type="text"
                  value={f.notes || ""}
                  onChange={(e) => updateFeeding(idx, "notes", e.target.value)}
                  placeholder="Tos al alimentar, fuga por estoma, medicamentos por sonda..."
                  className="h-9 text-sm"
                  data-testid={`peg-notes-${idx}`}
                />
              </div>
            </div>
          );
        })}
      </div>

      <Button
        type="button"
        variant="outline"
        onClick={addFeeding}
        className="h-11 w-full rounded-xl border-dashed border-slate-300 text-sm font-semibold text-slate-700 hover:border-amber-500 hover:bg-amber-50"
        data-testid="peg-add-row"
      >
        <Plus className="mr-1 h-4 w-4" /> Agregar toma
      </Button>

      {/* Shift totals */}
      {feedings.length > 0 && (
        <div
          className="rounded-xl border border-slate-200 bg-slate-50 p-3"
          data-testid="peg-shift-totals"
        >
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
            Subtotal del turno
          </p>
          <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
            <div>
              <div className="text-[10px] font-medium uppercase text-slate-500">Tomas</div>
              <div className="font-mono text-base font-bold text-slate-900">{totals.count}</div>
            </div>
            <div>
              <div className="text-[10px] font-medium uppercase text-slate-500">Formula</div>
              <div className="font-mono text-base font-bold text-slate-900">{totals.formula_ml} ml</div>
            </div>
            <div>
              <div className="text-[10px] font-medium uppercase text-slate-500">Agua (lavados)</div>
              <div className="font-mono text-base font-bold text-slate-900">{totals.water_ml} ml</div>
            </div>
            <div>
              <div className="text-[10px] font-medium uppercase text-slate-500">Total enteral</div>
              <div className="font-mono text-base font-bold text-blue-800" data-testid="peg-shift-total">
                {totals.total_ml} ml
              </div>
            </div>
          </div>
          {totals.intolerance_count > 0 && (
            <p className="mt-2 text-[11px] font-semibold text-red-700">
              {totals.intolerance_count} toma(s) con intolerancia registrada.
            </p>
          )}
          <p className="mt-1 text-[11px] italic text-slate-500">
            El acumulado de 24h se calcula en el resumen del paciente sumando todos los turnos del dia.
          </p>
        </div>
      )}

      {/* -------------------- Tube & stoma care (per shift) -------------------- */}
      <div
        className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
        data-testid="peg-tube-care"
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
            Cuidados de la sonda y estoma (1 vez por turno)
          </p>
          {tubeCare.alerted_at && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800"
              data-testid="peg-tube-alerted"
            >
              <Send className="h-3 w-3" /> Supervisor avisado
            </span>
          )}
        </div>

        <Label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">
          Estado del estoma (toca todos los que apliquen)
        </Label>
        <div className="mb-3 flex flex-wrap gap-1.5" data-testid="peg-stoma-options">
          {Object.entries(STOMA_LABELS).map(([k, label]) => {
            const active = (tubeCare.stoma_status || []).includes(k);
            const isBad = k !== "sano";
            return (
              <button
                key={k}
                type="button"
                onClick={() => toggleStoma(k)}
                className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  active
                    ? isBad
                      ? "border-red-500 bg-red-500 text-white"
                      : "border-emerald-500 bg-emerald-500 text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                }`}
                data-testid={`peg-stoma-${k}`}
              >
                {label}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <div>
            <Label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Marca externa (cm)</Label>
            <Input
              type="text"
              inputMode="decimal"
              value={tubeCare.external_length_cm || ""}
              onChange={(e) => updateTubeCare("external_length_cm", e.target.value)}
              placeholder="4.0"
              className="h-9 text-sm"
              data-testid="peg-external-length"
            />
            <p className="mt-0.5 text-[10px] italic text-slate-500">
              Si cambia vs. dias previos: posible migracion.
            </p>
          </div>
          <div>
            <Label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Permeabilidad</Label>
            <Select
              value={tubeCare.patency || ""}
              onValueChange={(v) => updateTubeCare("patency", v)}
            >
              <SelectTrigger
                className={`h-9 text-sm ${tubeCare.patency && tubeCare.patency !== "permeable" ? "border-red-400 bg-red-50 font-semibold text-red-800" : ""}`}
                data-testid="peg-patency"
              >
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PATENCY_LABELS).map(([k, label]) => (
                  <SelectItem key={k} value={k}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Sonda rotada</Label>
            <button
              type="button"
              onClick={() => updateTubeCare("tube_rotated", tubeCare.tube_rotated === true ? null : true)}
              className={`flex h-9 w-full items-center justify-center rounded-md border text-xs font-bold transition-colors ${
                tubeCare.tube_rotated
                  ? "border-emerald-400 bg-emerald-50 text-emerald-800"
                  : "border-slate-200 bg-white text-slate-500 hover:border-emerald-400"
              }`}
              data-testid="peg-tube-rotated"
            >
              {tubeCare.tube_rotated ? "Si" : "Marcar"}
            </button>
          </div>
          <div>
            <Label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Limpieza estoma</Label>
            <button
              type="button"
              onClick={() => updateTubeCare("stoma_cleaned", tubeCare.stoma_cleaned === true ? null : true)}
              className={`flex h-9 w-full items-center justify-center rounded-md border text-xs font-bold transition-colors ${
                tubeCare.stoma_cleaned
                  ? "border-emerald-400 bg-emerald-50 text-emerald-800"
                  : "border-slate-200 bg-white text-slate-500 hover:border-emerald-400"
              }`}
              data-testid="peg-stoma-cleaned"
            >
              {tubeCare.stoma_cleaned ? "Si" : "Marcar"}
            </button>
          </div>
        </div>

        <div className="mt-2">
          <Label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">Notas de la sonda</Label>
          <Input
            type="text"
            value={tubeCare.notes || ""}
            onChange={(e) => updateTubeCare("notes", e.target.value)}
            placeholder="Fijacion, balon, curacion realizada..."
            className="h-9 text-sm"
            data-testid="peg-tube-notes"
          />
        </div>
      </div>

      {/* Free-text observations for the shift */}
      <div>
        <Label className="mb-1 block text-xs font-bold uppercase text-slate-500">
          Observaciones del turno (PEG)
        </Label>
        <textarea
          value={data.observations || ""}
          onChange={(e) => onChange({ ...data, observations: e.target.value })}
          rows={2}
          placeholder="Tolerancia general, cambios en la piel, ajuste de horarios de tomas..."
          className="w-full rounded-md border border-slate-200 bg-white p-2 text-sm focus:border-[#1D4ED8] focus:outline-none focus:ring-1 focus:ring-[#1D4ED8]"
          data-testid="peg-observations"
        />
      </div>
    </div>
  );
}
